import { CrashConfig } from "./crash_config.js";
import { NativeBridge } from "./native_bridge.js";
import type { BreadcrumbType, CrashBreadcrumb } from "./types.js";

const MAX_DATA_CHARS = 200;
const MIRROR_DELAY_MS = 300;

export class CrashBreadcrumbs
{
  private static readonly ring: CrashBreadcrumb[] = [];
  private static mirror_timer: ReturnType<typeof setTimeout> | null = null;
  /** Set by PerfSession while it records, to put these events on its timeline. */
  public static listener: ( ( type: BreadcrumbType, data: string ) => void ) | null = null;

  /** Only to a recording PerfSession's timeline, not the crash ring: for events too frequent to spend crash breadcrumbs on. */
  public static trace( type: BreadcrumbType, data: string ): void
  {
    this.listener?.( type, data );
  }

  public static add( type: BreadcrumbType, data: string ): void
  {
    const text = data.length > MAX_DATA_CHARS ? data.slice( 0, MAX_DATA_CHARS ) : data;
    this.ring.push( { at: new Date().toISOString(), type, data: text } );
    while ( this.ring.length > CrashConfig.BREADCRUMB_LIMIT ) this.ring.shift();
    this.scheduleMirror();
    this.listener?.( type, data );
  }

  private static scheduleMirror(): void
  {
    if ( this.mirror_timer !== null || !NativeBridge.isAvailable() ) return;
    this.mirror_timer = setTimeout( () =>
    {
      this.mirror_timer = null;
      NativeBridge.breadcrumbs( this.ring );
    }, MIRROR_DELAY_MS );
  }

  public static http( method: string, url: string, status: number | string, elapsed_ms: number ): void
  {
    this.add( 'http', `${method.toUpperCase()} ${stripUrl( url )} ${status} ${Math.round( elapsed_ms )}ms` );
  }

  public static list(): CrashBreadcrumb[]
  {
    return this.ring.slice();
  }

  public static clear(): void
  {
    this.ring.length = 0;
    this.scheduleMirror();
  }
}

function stripUrl( url: string ): string
{
  try
  {
    const parsed = new URL( url, 'http://local' );
    return parsed.pathname;
  }
  catch
  {
    const q = url.indexOf( '?' );
    return q >= 0 ? url.slice( 0, q ) : url;
  }
}
